import type { CSSProperties, PointerEvent, ReactElement, RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DayPicker } from 'react-day-picker';
import { enUS, ja } from 'react-day-picker/locale';
import Datepicker from 'react-tailwindcss-datepicker';
import 'react-day-picker/style.css';
import './AnalysisPeriodCalendarPopover.css';
import type { PaginationBucket } from './periodUtils';
import { AnalysisMonthGridPicker } from './AnalysisMonthGridPicker';
import {
    buildAnalysisDatepickerConfigs,
    getPaidOnlyAnalysisShortcutDisplayTexts,
} from './analysisDatepickerConfigs';
import {
    getPeriodContainingDate,
    getMonthPeriodContainingDate,
    inclusiveLocalCalendarDayCount,
    startOfLocalDay,
    toDateStr,
    type PeriodRange,
} from './periodUtils';
import type { WeekStart } from '@/i18n/weekStart';
import type { ShortcutLabels } from '@/features/analysis/seach/util/Search';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    anchorRef: RefObject<HTMLElement | null>;
    bucket: PaginationBucket;
    periodStart: Date;
    periodEnd: Date;
    weekStart: WeekStart;
    locale: string;
    isPaid: boolean;
    onSelect: (range: PeriodRange) => void;
    /** 無料時: 有料専用ショートカットクリックでベーシック案内 */
    onPaidOnlyShortcutAttempt?: () => void;
    /** 無料時: カスタム期間が32日以上（両端含む）で確定しようとしたときの案内 */
    onLongCustomPeriodBlocked?: () => void;
}

const PANEL_Z = 10060;

/** LockIcon 相当（react-tailwindcss-datepicker のショートカット行は button ではなく li） */
const CUSTOM_SHORTCUT_LOCK_SVG = `<svg class="h-4 w-4 shrink-0 text-gray-600" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" stroke-width="2"/><path d="M9 10V8a3 3 0 0 1 6 0v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="15" r="1.4" fill="currentColor"/></svg>`;

function normalizeShortcutButtonLabel(raw: string): string {
    return raw.replace(/\s+/g, ' ').trim();
}

function clearShortcutLockDecorations(scope: ParentNode) {
    scope.querySelectorAll('[data-kotomil-shortcut-locked="1"]').forEach((el) => {
        el.removeAttribute('data-kotomil-shortcut-locked');
        el.removeAttribute('aria-disabled');
        el.classList.remove(
            'kotomil-analysis-shortcut-locked',
            'inline-flex',
            '!inline-flex',
            'items-center',
            'gap-1.5',
            'max-w-full',
        );
    });
    scope.querySelectorAll('[data-kotomil-lock-suffix="1"]').forEach((el) => el.remove());
}

export function AnalysisPeriodCalendarPopover({
    isOpen,
    onClose,
    anchorRef,
    bucket,
    periodStart,
    periodEnd,
    weekStart,
    locale,
    isPaid,
    onSelect,
    onPaidOnlyShortcutAttempt,
    onLongCustomPeriodBlocked,
}: Props): ReactElement | null {
    const panelRef = useRef<HTMLDivElement>(null);
    const datepickerMountRef = useRef<HTMLDivElement>(null);
    /** カスタムレンジ: 日付グリッドのクリック回数（終了日確定までの誤 onChange を弾く） */
    const customRangeDayPointerCountRef = useRef(0);
    /** 直近の確定操作がショートカット（li）由来か（1クリックで onChange が来る） */
    const customRangeLastPickWasShortcutRef = useRef(false);
    const [panelStyle, setPanelStyle] = useState<CSSProperties>({});

    const { t: tSearch } = useTranslation('search');

    useEffect(() => {
        if (isOpen && bucket === 'custom') {
            customRangeDayPointerCountRef.current = 0;
            customRangeLastPickWasShortcutRef.current = false;
        }
    }, [isOpen, bucket]);

    const handleCustomPickerPointerDownCapture = useCallback(
        (e: PointerEvent<HTMLDivElement>) => {
            if (bucket !== 'custom' || !isOpen) return;
            const root = datepickerMountRef.current;
            if (!root) return;
            const el = e.target;
            if (!(el instanceof HTMLElement) || !root.contains(el)) return;

            // react-tailwindcss-datepicker のショートカット一覧（ul.w-full.tracking-wide）
            if (el.closest('ul.w-full.tracking-wide')) {
                customRangeLastPickWasShortcutRef.current = true;
                return;
            }

            const btn = el.closest('button');
            if (!btn || !root.contains(btn)) return;
            // 日付セルは 7 列グリッド内の button（月切替などは別レイアウト）
            if (!btn.closest('.grid.grid-cols-7')) return;

            customRangeLastPickWasShortcutRef.current = false;
            customRangeDayPointerCountRef.current += 1;
        },
        [bucket, isOpen],
    );

    const isJa = locale.startsWith('ja');
    const rdpLocale = isJa ? ja : enUS;
    const weekStartsOn = weekStart === 'mon' ? 1 : 0;

    const selectedDay = useMemo(() => {
        if (bucket === 'custom') return undefined;
        return periodStart;
    }, [bucket, periodStart]);

    const weekRangeModifier = useMemo(
        () => ({ from: periodStart, to: periodEnd } as const),
        [periodStart, periodEnd],
    );

    const shortcutLabels = tSearch('shortcuts', { returnObjects: true }) as ShortcutLabels;

    const paidOnlyShortcutLabels = useMemo(
        () => getPaidOnlyAnalysisShortcutDisplayTexts(shortcutLabels),
        [shortcutLabels],
    );

    const tailwindConfigs = useMemo(
        () => buildAnalysisDatepickerConfigs(weekStart, shortcutLabels),
        [weekStart, shortcutLabels],
    );

    const tailwindValue = useMemo(
        () => ({
            startDate: startOfLocalDay(periodStart),
            endDate: startOfLocalDay(periodEnd),
        }),
        [periodStart, periodEnd],
    );

    // 無料: 有料専用ショートカットにロック表示（ラベル一致で判定）
    useLayoutEffect(() => {
        if (!isOpen || bucket !== 'custom') return;
        const rootEl = datepickerMountRef.current;
        if (!rootEl) return;

        const apply = () => {
            const popup = rootEl.querySelector('.kotomil-analysis-custom-dp-popup');
            if (!popup) return;

            if (isPaid || paidOnlyShortcutLabels.length === 0) {
                clearShortcutLockDecorations(popup);
                return;
            }

            // ライブラリはショートカットを <ul class="w-full tracking-wide ..."> 直下の <li> で描画する
            const shortcutUl = popup.querySelector('ul.w-full.tracking-wide');
            if (!shortcutUl) return;

            const items = shortcutUl.querySelectorAll<HTMLLIElement>(':scope > li');
            items.forEach((li) => {
                const text = normalizeShortcutButtonLabel(li.textContent ?? '');
                const locked = paidOnlyShortcutLabels.some(
                    (label) => normalizeShortcutButtonLabel(label) === text,
                );
                if (!locked) {
                    li.removeAttribute('data-kotomil-shortcut-locked');
                    li.removeAttribute('aria-disabled');
                    li.classList.remove(
                        'kotomil-analysis-shortcut-locked',
                        'inline-flex',
                        '!inline-flex',
                        'items-center',
                        'gap-1.5',
                        'max-w-full',
                    );
                    li.querySelector('[data-kotomil-lock-suffix="1"]')?.remove();
                    return;
                }
                li.setAttribute('data-kotomil-shortcut-locked', '1');
                li.setAttribute('aria-disabled', 'true');
                li.classList.add(
                    'kotomil-analysis-shortcut-locked',
                    '!inline-flex',
                    'items-center',
                    'gap-1.5',
                    'max-w-full',
                );
                if (!li.querySelector('[data-kotomil-lock-suffix="1"]')) {
                    const span = document.createElement('span');
                    span.setAttribute('data-kotomil-lock-suffix', '1');
                    span.className =
                        'inline-flex shrink-0 items-center justify-center align-middle';
                    span.innerHTML = CUSTOM_SHORTCUT_LOCK_SVG;
                    li.appendChild(span);
                }
            });
        };

        apply();
        const raf = requestAnimationFrame(apply);
        const mo = new MutationObserver(apply);
        mo.observe(rootEl, { subtree: true, childList: true, characterData: true });
        return () => {
            cancelAnimationFrame(raf);
            mo.disconnect();
            const popup = rootEl.querySelector('.kotomil-analysis-custom-dp-popup');
            if (popup) clearShortcutLockDecorations(popup);
        };
    }, [isOpen, bucket, isPaid, paidOnlyShortcutLabels]);

    useEffect(() => {
        if (!isOpen || bucket !== 'custom' || isPaid || !onPaidOnlyShortcutAttempt) return;
        const rootEl = datepickerMountRef.current;
        if (!rootEl) return;

        const onClickCapture = (e: MouseEvent) => {
            const t = e.target as HTMLElement | null;
            const li = t?.closest?.('li[data-kotomil-shortcut-locked="1"]');
            if (!li || !rootEl.contains(li)) return;
            e.preventDefault();
            e.stopPropagation();
            onPaidOnlyShortcutAttempt();
        };

        rootEl.addEventListener('click', onClickCapture, true);
        return () => rootEl.removeEventListener('click', onClickCapture, true);
    }, [isOpen, bucket, isPaid, onPaidOnlyShortcutAttempt]);

    useLayoutEffect(() => {
        if (!isOpen) return;

        const updatePosition = () => {
            const anchor = anchorRef.current;
            if (!anchor) return;
            const r = anchor.getBoundingClientRect();
            const narrow = window.matchMedia('(max-width: 639px)').matches;
            if (narrow) {
                const marginH = 12;
                const marginBottom = 12;
                const gap = 8;
                const vpH = window.innerHeight;
                const roughPanelH =
                    bucket === 'custom' ? 400 : bucket === 'month' ? 300 : 320;
                const spaceBelow = vpH - r.bottom - gap;
                const spaceAbove = r.top - gap;
                const placeBelow =
                    spaceBelow >= Math.min(roughPanelH, 260) || spaceBelow >= spaceAbove;

                const weekOrMonthCentered = bucket === 'week' || bucket === 'month';
                const baseNarrow = weekOrMonthCentered
                    ? {
                          left: '50%',
                          right: 'auto',
                          transform: 'translateX(-50%)',
                          maxWidth: 'calc(100vw - 1.5rem)',
                      }
                    : {
                          left: marginH,
                          right: marginH,
                          transform: 'none',
                      };

                if (placeBelow) {
                    const topPx = r.bottom + gap;
                    const maxH = Math.max(200, vpH - topPx - marginBottom);
                    setPanelStyle({
                        position: 'fixed',
                        ...baseNarrow,
                        top: topPx,
                        bottom: 'auto',
                        maxHeight: maxH,
                        overflowY: 'auto',
                        overscrollBehavior: 'contain',
                        WebkitOverflowScrolling: 'touch',
                    });
                } else {
                    const bottomPx = vpH - r.top + gap;
                    const maxH = Math.max(200, r.top - marginH);
                    setPanelStyle({
                        position: 'fixed',
                        ...baseNarrow,
                        top: 'auto',
                        bottom: bottomPx,
                        maxHeight: maxH,
                        overflowY: 'auto',
                        overscrollBehavior: 'contain',
                        WebkitOverflowScrolling: 'touch',
                    });
                }
            } else {
                const center = r.left + r.width / 2;
                const estimatedWidth = bucket === 'custom' ? 760 : 320;
                const minLeft = estimatedWidth / 2 + 16;
                const maxLeft = window.innerWidth - estimatedWidth / 2 - 16;
                
                let safeLeft = center;
                if (safeLeft < minLeft) safeLeft = minLeft;
                if (safeLeft > maxLeft) safeLeft = maxLeft;

                setPanelStyle({
                    position: 'fixed',
                    top: r.bottom + 8,
                    left: safeLeft,
                    transform: 'translateX(-50%)',
                });
            }
        };

        updatePosition();
        let rafOuter = 0;
        let rafInner = 0;
        rafOuter = requestAnimationFrame(() => {
            rafInner = requestAnimationFrame(updatePosition);
        });
        window.addEventListener('resize', updatePosition);
        window.visualViewport?.addEventListener('resize', updatePosition);
        window.visualViewport?.addEventListener('scroll', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            cancelAnimationFrame(rafOuter);
            cancelAnimationFrame(rafInner);
            window.removeEventListener('resize', updatePosition);
            window.visualViewport?.removeEventListener('resize', updatePosition);
            window.visualViewport?.removeEventListener('scroll', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, anchorRef, bucket]);

    useLayoutEffect(() => {
        if (!isOpen || bucket !== 'custom' || typeof document === 'undefined') return;
        const root = datepickerMountRef.current;
        if (!root) return;
        const id = window.requestAnimationFrame(() => {
            const input = root.querySelector<HTMLInputElement>('input[autocomplete="off"]');
            input?.focus();
        });
        return () => window.cancelAnimationFrame(id);
    }, [isOpen, bucket, periodStart, periodEnd]);

    const pickWeekContaining = (d: Date) => {
        onSelect(getPeriodContainingDate(d, weekStart));
        onClose();
    };

    const pickMonthContaining = (d: Date) => {
        onSelect(getMonthPeriodContainingDate(d));
        onClose();
    };

    const applyCustomRange = (start: Date, end: Date) => {
        const s = startOfLocalDay(start);
        const e = startOfLocalDay(end);
        if (s.getTime() > e.getTime()) return;

        const days = inclusiveLocalCalendarDayCount(s, e);
        const pickViaShortcut = customRangeLastPickWasShortcutRef.current;
        const dayPointerCount = customRangeDayPointerCountRef.current;

        const resetCustomRangePickTracking = () => {
            customRangeDayPointerCountRef.current = 0;
            customRangeLastPickWasShortcutRef.current = false;
        };

        if (!isPaid && onLongCustomPeriodBlocked && days >= 32) {
            // ライブラリが開始クリック時に古い終了日付きで onChange を出すことがあるため、
            // カレンダー操作では「終了日を含む2回目の日付クリック」以降だけ案内する。
            if (!pickViaShortcut && dayPointerCount < 2) {
                return;
            }
            resetCustomRangePickTracking();
            onLongCustomPeriodBlocked();
            return;
        }

        resetCustomRangePickTracking();
        onSelect({ start: s, end: e });
        onClose();
    };

    if (!isOpen || typeof document === 'undefined') {
        return null;
    }

    const panelClassName =
        'kotomil-period-cal w-max max-w-[calc(100vw-1.5rem)] rounded-xl border border-gray-200 bg-white p-3 shadow-xl max-sm:pb-[max(0.75rem,env(safe-area-inset-bottom,0px))]';

    return createPortal(
        <>
            <div
                className="fixed inset-0 z-[10055] bg-black/30 sm:bg-transparent"
                aria-hidden
                onPointerDown={(e) => {
                    e.preventDefault();
                    onClose();
                }}
            />
            <div
                ref={panelRef}
                style={{ ...panelStyle, zIndex: PANEL_Z }}
                className={panelClassName}
                role="dialog"
                aria-modal="true"
                onPointerDown={(e) => e.stopPropagation()}
            >
                {bucket === 'week' ? (
                    <DayPicker
                        key={`w-${toDateStr(periodStart)}`}
                        mode="single"
                        required={false}
                        selected={selectedDay}
                        onSelect={(_date, triggerDate) => {
                            pickWeekContaining(triggerDate);
                        }}
                        locale={rdpLocale}
                        weekStartsOn={weekStartsOn}
                        defaultMonth={periodStart}
                        showOutsideDays
                        modifiers={{
                            inSelectedWeek: weekRangeModifier,
                        }}
                        modifiersClassNames={{
                            inSelectedWeek: 'kotomil-rdp-week-range',
                        }}
                        className="m-0 font-sans text-sm"
                    />
                ) : bucket === 'month' ? (
                    <AnalysisMonthGridPicker
                        key={`m-${toDateStr(periodStart)}`}
                        periodStart={periodStart}
                        locale={locale}
                        onSelectMonth={(d) => {
                            pickMonthContaining(d);
                            onClose();
                        }}
                    />
                ) : (
                    <div
                        ref={datepickerMountRef}
                        className="kotomil-analysis-custom-dp w-full min-w-0"
                        onPointerDownCapture={handleCustomPickerPointerDownCapture}
                    >
                        <Datepicker
                            key={`c-${toDateStr(periodStart)}-${toDateStr(periodEnd)}`}
                            value={tailwindValue}
                            onChange={(val) => {
                                if (val?.startDate && val?.endDate) {
                                    applyCustomRange(val.startDate, val.endDate);
                                }
                            }}
                            useRange
                            showShortcuts
                            showFooter={false}
                            configs={tailwindConfigs}
                            i18n={isJa ? 'ja' : 'en'}
                            startWeekOn={weekStart === 'mon' ? 'mon' : 'sun'}
                            primaryColor="yellow"
                            displayFormat="YYYY-MM-DD"
                            separator="~"
                            readOnly
                            popoverDirection="down"
                            containerClassName="relative z-0 w-full"
                            popupClassName="kotomil-analysis-custom-dp-popup z-[10070]"
                        />
                    </div>
                )}
            </div>
        </>,
        document.body,
    );
}
